import { useEffect } from 'react';
import { useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { blockMultiCards } from 'api/cards/cards';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useSelectedCard } from 'hooks/useSelectedCard/useSelectedCard';
import { BlockCardBody } from 'api/cards/cards.types';
import { useMutation } from 'hooks/useMutation/useMutation';
import { CardBlockTypeEnum } from 'api/cards/cards.enum';
import { useModalState } from 'hooks/useModalState/useModalState';

import { BlockMultiCards } from './BlockMultiCards';
import { BlockMultiCardsContainerProps } from './BlockMultiCards.types';
import { useBlockMultiCardsValidation } from './BlockMultiCards.validation';

export const BlockMultiCardsContainer = ({ checkedCards }: BlockMultiCardsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { selectedCard, setSelectedCard } = useSelectedCard();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const { mutate: onBlockMultiCards, isLoading } = useMutation(blockMultiCards, {
    onSuccess: (_, { body }) => {
      if (selectedCard && checkedCards.some(({ id }) => id === selectedCard?.id)) {
        setSelectedCard({ ...selectedCard, ...body, blokada: CardBlockTypeEnum.RECZNA });
      }
      queryClient.invalidateQueries([QueryKeyEnum.CARDS]);

      toast.dark(formatMessage({ id: AppMessages['cards.multiAction.block.success'] }));
      onModalClose();
    },
  });

  const { schema } = useBlockMultiCardsValidation();
  const formMethods = useForm<BlockCardBody>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  useEffect(() => {
    formMethods.reset({ powodBlokady: '' });
  }, [isModalOpen]);

  const onSubmit = (formBody: BlockCardBody) => {
    onBlockMultiCards({ body: { ...formBody, cards: checkedCards.map(({ id }) => id) } });
  };

  return (
    <FormProvider {...formMethods}>
      <BlockMultiCards
        checkedCards={checkedCards}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
